import React, { useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';

const sourceKey = (publication, participant) => `${participant.identity}:${publication.trackSid}`;

function AudioSource({ source, context, onChange }) {
  const audioRef = useRef(null);
  const meterRef = useRef(null);
  const { track, selected, volume, muted, label, participantLabel, error } = source;

  useEffect(() => {
    if (!track || !selected) return;
    const element = audioRef.current;
    const meter = meterRef.current;
    track.attach(element);
    let input;
    let analyser;
    let timer;
    try {
      if (context && track.mediaStreamTrack) {
        input = context.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
        analyser = context.createAnalyser();
        analyser.fftSize = 256;
        input.connect(analyser);
        const samples = new Uint8Array(analyser.fftSize);
        timer = setInterval(() => {
          analyser.getByteTimeDomainData(samples);
          const rms = Math.sqrt(samples.reduce((sum, sample) => sum + ((sample - 128) / 128) ** 2, 0) / samples.length);
          if (meter) meter.value = Math.min(100, rms * 200);
        }, 100);
      }
    } catch {
      // Playback remains available if this browser cannot analyse a remote stream.
    }
    return () => {
      clearInterval(timer);
      input?.disconnect();
      analyser?.disconnect();
      track.detach(element);
      if (meter) meter.value = 0;
    };
  }, [track, selected, context]);

  useEffect(() => { track?.setVolume(selected ? volume : 0); }, [track, selected, volume]);

  return <li className="monitor-audio-source">
    <label className="monitor-audio-source-label">
      <input type="checkbox" checked={selected} onChange={(event) => onChange({ selected: event.target.checked })} />
      <span><strong>{label}</strong><small>{participantLabel}</small></span>
    </label>
    <span className="monitor-audio-source-state">{error || (muted ? 'Muted by participant' : !selected ? 'Not listening' : track ? 'Live audio' : 'Waiting for audio…')}</span>
    <meter ref={meterRef} min="0" max="100" value="0" aria-label={`Audio level: ${label}`} />
    <label className="monitor-audio-volume">Volume
      <input type="range" min="0" max="1" step="0.05" value={volume} disabled={!selected}
        aria-label={`Volume: ${label}`} onChange={(event) => onChange({ volume: Number(event.target.value) })} />
    </label>
    <audio ref={audioRef} hidden />
  </li>;
}

function dispose(session) {
  if (session.disposed) return;
  session.disposed = true;
  session.abort.abort();
  clearTimeout(session.timeout);
  session.room.removeAllListeners();
  void session.room.disconnect().catch(() => {});
  if (session.context && session.context.state !== 'closed') void session.context.close().catch(() => {});
}

export default function LiveKitRoomAudio({ roomId, user, onAccessError }) {
  const sessionRef = useRef(null);
  const [sources, setSources] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [playbackBlocked, setPlaybackBlocked] = useState(false);

  useEffect(() => {
    setSources([]);
    setStatus('idle');
    setError('');
    setPlaybackBlocked(false);
    return () => {
      const session = sessionRef.current;
      sessionRef.current = null;
      if (session) dispose(session);
    };
  }, [roomId, user]);

  const stop = () => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) dispose(session);
    setSources([]);
    setStatus('idle');
    setPlaybackBlocked(false);
  };

  const enablePlayback = () => {
    const session = sessionRef.current;
    if (!session) return;
    void session.context?.resume().catch(() => {});
    void session.room.startAudio().then(() => {
      if (sessionRef.current === session) setPlaybackBlocked(false);
    }).catch(() => {
      if (sessionRef.current === session) setPlaybackBlocked(true);
    });
  };

  const start = async () => {
    if (!user || !roomId || sessionRef.current) return;
    setError('');
    setStatus('connecting');
    let session;
    try {
      const room = new Room();
      session = { room, abort: new AbortController(), sources: new Map(), context: null, disposed: false };
      sessionRef.current = session;
      const current = () => sessionRef.current === session && !session.disposed;
      const refresh = () => { if (current()) setSources([...session.sources.values()]); };
      const upsert = (publication, participant, track) => {
        if (!current() || publication.kind !== Track.Kind.Audio) return;
        const key = sourceKey(publication, participant);
        const previous = session.sources.get(key);
        const next = {
          key, publication, participantId: participant.identity,
          label: publication.trackName || 'Microphone',
          participantLabel: participant.attributes?.userName || participant.attributes?.avaName || participant.name || participant.identity,
          selected: previous?.selected ?? true, volume: previous?.volume ?? 1,
          track: track === undefined ? previous?.track || publication.track : track,
          muted: publication.isMuted, error: previous?.error || '',
        };
        session.sources.set(key, next);
        if (!previous) publication.setSubscribed(next.selected);
        refresh();
      };
      const remove = (publication, participant) => {
        session.sources.delete(sourceKey(publication, participant));
        refresh();
      };
      room.on(RoomEvent.TrackPublished, upsert);
      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        upsert(publication, participant, track);
        const source = session.sources.get(sourceKey(publication, participant));
        if (source) source.error = '';
        refresh();
      });
      room.on(RoomEvent.TrackUnsubscribed, (_track, publication, participant) => upsert(publication, participant, null));
      room.on(RoomEvent.TrackUnpublished, remove);
      room.on(RoomEvent.TrackMuted, (publication, participant) => upsert(publication, participant));
      room.on(RoomEvent.TrackUnmuted, (publication, participant) => upsert(publication, participant));
      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        for (const source of session.sources.values()) if (source.participantId === participant.identity) session.sources.delete(source.key);
        refresh();
      });
      room.on(RoomEvent.TrackSubscriptionFailed, (trackSid, participant) => {
        const source = session.sources.get(`${participant.identity}:${trackSid}`);
        if (source) source.error = 'Audio subscription failed. Toggle this source to retry.';
        refresh();
      });
      room.on(RoomEvent.TrackSubscriptionPermissionChanged, (publication, _status, participant) => {
        const source = session.sources.get(sourceKey(publication, participant));
        if (source) source.error = publication.permissionStatus === 'not_allowed' ? 'Audio access denied' : '';
        refresh();
      });
      room.on(RoomEvent.ConnectionStateChanged, (state) => {
        if (current()) setStatus(state === 'connected' ? 'listening' : state === 'reconnecting' ? 'reconnecting' : 'connecting');
      });
      room.on(RoomEvent.AudioPlaybackStatusChanged, () => { if (current()) setPlaybackBlocked(!room.canPlaybackAudio); });
      room.on(RoomEvent.Disconnected, () => {
        if (!current()) return;
        stop();
        setError('Audio disconnected. Start listening again to reconnect.');
      });
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) session.context = new AudioContext();
      } catch { /* Audio playback can work without a visual level meter. */ }
      // Unlock playback while the Start click still grants browser user activation.
      enablePlayback();
      const firebaseToken = await user.getIdToken();
      if (!current()) return;
      session.timeout = setTimeout(() => session.abort.abort(), 15000);
      const response = await fetch('/monitoring/api/listen', {
        method: 'POST', headers: { Authorization: `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId }), signal: session.abort.signal, cache: 'no-store',
      });
      const body = await response.json().catch(() => null);
      clearTimeout(session.timeout);
      if (!current()) return;
      if (!response.ok) {
        const failure = new Error(body?.error || `Audio access failed (HTTP ${response.status}).`);
        failure.status = response.status;
        throw failure;
      }
      if (!body?.url || !body?.token || body.roomId !== roomId) throw new Error('Audio access returned an invalid response.');
      await room.connect(body.url, body.token, { autoSubscribe: false });
      if (!current()) {
        // A cancelled connect can finish after its first disconnect call.
        await room.disconnect();
        return;
      }
      for (const participant of room.remoteParticipants.values()) {
        for (const publication of participant.trackPublications.values()) upsert(publication, participant);
      }
      setStatus('listening');
      setPlaybackBlocked(!room.canPlaybackAudio);
    } catch (failure) {
      if (session && sessionRef.current !== session) return;
      stop();
      setError(failure.name === 'AbortError' ? 'Audio access timed out. Please try again.' : failure.message || 'Could not start audio.');
      if ([401, 403].includes(failure.status)) onAccessError?.(failure);
    }
  };

  const changeSource = (key, changes) => {
    const session = sessionRef.current;
    const source = session?.sources.get(key);
    if (!source) return;
    Object.assign(source, changes);
    if ('selected' in changes) {
      source.error = '';
      source.publication.setSubscribed(changes.selected);
    }
    setSources([...session.sources.values()]);
  };

  return <section className="monitor-audio" aria-label="Live room audio">
    <div className="monitor-audio-actions">
      {status === 'idle'
        ? <button onClick={start} disabled={!user || !roomId}>Start listening</button>
        : <button onClick={stop}>Stop listening</button>}
      <span role="status">{status === 'listening' ? 'Listening live' : status === 'reconnecting' ? 'Reconnecting…' : status === 'connecting' ? 'Connecting…' : 'Audio is off'}</span>
      {playbackBlocked && status !== 'idle' && <button onClick={enablePlayback}>Enable playback</button>}
    </div>
    {error && <p role="alert" className="monitor-error">{error}</p>}
    <p className="monitor-muted">Listen to one or more published audio sources. Your microphone stays off. Audio is not recorded.</p>
    {sources.length > 0
      ? <ul className="monitor-audio-list">{sources.map((source) => <AudioSource key={source.key} source={source}
        context={sessionRef.current?.context} onChange={(changes) => changeSource(source.key, changes)} />)}</ul>
      : status === 'listening' && <p className="monitor-empty">No audio is currently published in this room. New sources appear automatically.</p>}
  </section>;
}
